/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Page Reports Constructor Template List component
 *
 * @author Evgeniy Naumov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { useRef, useState } from 'react';
import type { FC } from 'react';

import { ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@material-ui/core';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { icons } from '@assets/index';
import { ButtonImageStyled } from '@components/common/Button/styled/ButtonStyled';
import { makeSelectPageReportsConstructorTranslation } from '@modules/internationalization/intSelectors';
import { modalOpen } from '@modules/modal/modalActions';
import { RoutePath } from '@routes/constants/routesConstants';
import { stringFormat } from '@utils/stringFormat';

import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { EMode, EConstructorItem } from '../constants';
import { reportsConstructorCurrentRowItemSelector, reportsConstructorSelectedItemSelector } from '../selector';
import { setCurrentRowItem, setSelectedItem } from '../slice';
import { TemplateId } from '../types';
import type { IConstructorTemplate } from '../types';
import ConstructorScheduleButton from './ConstructorScheduleButton';
import {
    ConstructorCardStyled,
    ConstructorCardContainerStyled,
    ConstrucotorCardTitleStyled,
    ConstrucotorCardSubtitleStyled,
    ConstrucotorCardDescriptionStyled,
    ConstrucotorMenuContainerStyled,
} from './styled/ConstructorStyled';
import { TemplateNameModal } from './TemplateNameModal';
import { TemplateRemoveModal } from './TemplateRemoveModal';

interface IConstructorTemplatesCardProps {
    template: IConstructorTemplate;
    rowId: string;
}

const ConstructorTemplateCard: FC<IConstructorTemplatesCardProps> = ({ template, rowId }) => {
    const { id, name, createDate, type, schedule } = template;
    const history = useHistory();
    const dispatch = useDispatch();

    const [menuIsOpen, setMenuIsOpen] = useState(false);

    const tReportsConstructor = useSelector(makeSelectPageReportsConstructorTranslation());
    const currentRowItem = useSelector(reportsConstructorCurrentRowItemSelector);
    const selectedItem = useSelector(reportsConstructorSelectedItemSelector);

    const menuAnchorEl = useRef<HTMLImageElement | null>(null);

    const handleMenuOpen = () => setMenuIsOpen(true);
    const handleMenuClose = () => setMenuIsOpen(false);

    const handleTemplateCardClick = () => {
        dispatch(setSelectedItem({ id, reportType: type, itemType: EConstructorItem.TEMPLATE }));
    };

    const handleRenameTemplate = () => {
        dispatch(
            modalOpen({
                modalParams: {
                    isCustomCrossButton: true,
                },
                render: ({ onCloseModal }) => (
                    <TemplateNameModal mode={EMode.EDIT} templateId={TemplateId(id)} onCloseModal={onCloseModal} />
                ),
            }),
        );
    };

    const handleEditTemplate = () => {
        history.push(history.location.pathname + RoutePath.wizardOrderTemplateEdit, {
            template: selectedItem,
            mode: 'TEMPLATE_EDIT',
        });
    };

    const handleRemoveTemplate = () => {
        dispatch(
            modalOpen({
                modalParams: {
                    isCustomCrossButton: true,
                },
                render: ({ onCloseModal }) => (
                    <TemplateRemoveModal templateId={TemplateId(id)} title={name} onCloseModal={onCloseModal} />
                ),
            }),
        );
    };
    const handleSheduleClick = () => {
        if (currentRowItem && currentRowItem.rowItemId === id && currentRowItem.rowId === rowId) {
            dispatch(setCurrentRowItem(null));
        } else {
            dispatch(
                setCurrentRowItem({
                    rowId,
                    rowItemId: id,
                    rowType: EConstructorItem.TEMPLATE,
                    selectedItem: { id, reportType: type, itemType: EConstructorItem.TEMPLATE },
                }),
            );
        }
    };

    return (
        <ConstructorCardStyled
            selected={selectedItem?.itemType === EConstructorItem.TEMPLATE && id === selectedItem?.id}
            onClick={handleTemplateCardClick}
        >
            <ConstructorCardContainerStyled>
                <ConstrucotorCardTitleStyled>{name}</ConstrucotorCardTitleStyled>
                <ConstrucotorCardSubtitleStyled>
                    {stringFormat(
                        tReportsConstructor.common.created,
                        dayjs(createDate).format(EFormatData.FullData),
                    )}
                </ConstrucotorCardSubtitleStyled>
                <ConstrucotorCardDescriptionStyled>
                    {tReportsConstructor.reportTemplates[type].typeDescription}
                </ConstrucotorCardDescriptionStyled>
                {schedule && (
                    <ConstructorScheduleButton onClick={handleSheduleClick}>
                        {tReportsConstructor.common.schedule}
                    </ConstructorScheduleButton>
                )}
            </ConstructorCardContainerStyled>
            <ConstrucotorMenuContainerStyled>
                <ButtonImageStyled alt="menu-icon" ref={menuAnchorEl} src={icons.densityOn} onClick={handleMenuOpen} />
                <Popper
                    disablePortal
                    transition
                    anchorEl={menuAnchorEl.current}
                    open={menuIsOpen}
                    placement="bottom-end"
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                            <Paper elevation={8} style={{ zIndex: 2 }}>
                                <ClickAwayListener onClickAway={handleMenuClose}>
                                    <MenuList>
                                        <MenuItem onClick={handleEditTemplate}>
                                            {tReportsConstructor.common.menu.edit}
                                        </MenuItem>
                                        <MenuItem onClick={handleRenameTemplate}>
                                            {tReportsConstructor.common.menu.rename}
                                        </MenuItem>
                                        <MenuItem onClick={handleRemoveTemplate}>
                                            {tReportsConstructor.common.menu.delete}
                                        </MenuItem>
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            </ConstrucotorMenuContainerStyled>
        </ConstructorCardStyled>
    );
};

export default ConstructorTemplateCard;
